import {
  AddOnFilterModel,
  useAddOnFilter,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import {
  ColumnData,
  ListCountProvider,
  SelectionStatus,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { AddOnsFilters } from './addOnsFilters';
import { AddOnsList, SelectedAddonRow } from './addOnsList';
import { AddOnsSelectionPanelMain } from './addOnsSelectionPanel';

export const AddOns = () => {
  return <AddOnsBase />;
};

export const AddOnsBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { data: addOnFilter } = useAddOnFilter();

  const impersonatingWorkspace = useCurrentWorkspace(true);
  const currentWorkspace = useCurrentWorkspace();

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    const filter: AddOnFilterModel = addOnFilter ?? new AddOnFilterModel();
    filter.workspaceId = currentWorkspace?.id;
    return filter.getGraphQLFilter();
  }, [addOnFilter, currentWorkspace?.id]);

  const [rowsSelected, setRowsSelected] = useState<SelectedAddonRow[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const filterComponent = <AddOnsFilters />;
  const [count, setCount] = useState<number>();

  const columnComponent = (
    <View style={styles.container}>
      <AddOnsList
        filter={filter}
        showCheckboxColumn={showCheckboxColumn}
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        onItemsCountChange={setCount}
      />
    </View>
  );

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('addOn.topBar.addonSelected', {
            defaultValue: '1 add-on selected',
          })
        : t('addOn.topBar.addonsSelected', {
            count,
            defaultValue: `${count} add-ons selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AddOnsSelectionPanelMain
          addonIds={rowsSelectedIds}
          onClose={clearSelection}
        />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    rowsSelected,
    rowsSelectedIds,
    clearSelection,
    t,
    i18n,
  ]);

  // Selection panel takes priority; fall back to the caller-injected override
  // (used by the /add-ons/filters sibling route to show filters in the right
  // column without re-mounting this screen).
  const resolvedRightColumn = selectionRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride ? ViewMode.DUAL : ViewMode.SINGLE);

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={{
          title: 'addOn.list.addOns',
          count,
          mainComponent: columnComponent,
          headerComponent: filterComponent,
        }}
        rightColumn={resolvedRightColumn}
        viewMode={resolvedViewMode}
      />
    </ListCountProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
});
